import { capitalizeAll, dataURItoBlob } from '../helpers/formatters'
import { useQuery } from '@tanstack/react-query'
import { useToken } from './useToken'
import { fetchCertificates, UserCertificatesDTO } from '../infrastructure/api-signup'
import DayJS from '../helpers/DayJS'
import { useDownloadFile } from './useDownloadFile'
import { useState } from 'react'
import { AwsCertificates } from '../infrastructure/api-aws'

export const useCertificates = () => {
    const { _id: userId } = useToken()
    const { download } = useDownloadFile()

    const [isDownloading, setIsDownloading] = useState(false)
    const [isSharingToLinkedIn, setIsSharingToLinkedIn] = useState(false)

    const { data: certificates } = useQuery({
        queryKey: ['User', userId, 'Certificates'],
        queryFn: () => fetchCertificates(),
        staleTime: 60 * 60 * 1000, // 1 hour
        enabled: !!userId,
    })

    const downloadPDFCertificate = (certificate: UserCertificatesDTO) => {
        setIsDownloading(true)
        AwsCertificates.get<LambdaResponse>(`/certs/pdf?${getQueryParams(certificate)}`)
            .then(({ data: { body } }) => {
                const fileName = `${certificate.title}.pdf`
                const uri = body.data
                const blob = dataURItoBlob(uri)
                download(blob, fileName, 'application/pdf')
            })
            .finally(() => setIsDownloading(false))
    }

    const shareToLinkedIn = async (certificate: UserCertificatesDTO) => {
        setIsSharingToLinkedIn(true)
        const { data } = await AwsCertificates.get<LambdaResponse>(`/certs/toS3?${getQueryParams(certificate)}`)
        setIsSharingToLinkedIn(false)
        const url = parseLinkedInCertURL(certificate, data.body.data)
        window.open(url)
    }

    return {
        certificates,
        downloadPDFCertificate,
        shareToLinkedIn,
        isDownloading,
        isSharingToLinkedIn,
    }
}

const parseLinkedInCertURL = ({ id, title, issueDate }: UserCertificatesDTO, url: string) => {
    const name = encodeURIComponent(capitalizeAll(title))
    const issueMonth = DayJS(issueDate).month() + 1
    const issueYear = DayJS(issueDate).year()
    const certUrl = encodeURIComponent(url)
    return `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${name}&issueMonth=${issueMonth}&issueYear=${issueYear}&certId=${id}&certUrl=${certUrl}&organizationId=22343212&trk=login_reg_redirect`
}

const getQueryParams = (certificate: UserCertificatesDTO) => {
    const { profileId, name, lastname, course, idd, content, issueDate, template, trayecto, unidadAcademica } = certificate
    const params = new URLSearchParams()
    params.set('profileId', profileId)
    params.set('name', name)
    params.set('lastname', lastname)
    params.set('course', course ?? '')
    params.set('idd', idd ?? '')
    params.set('content', content ?? '')
    params.set('date', DayJS(issueDate).format('LL'))
    params.set('template', template ?? 'template_d')
    params.set('trayecto', trayecto ?? '')
    params.set('unidad_academica', unidadAcademica ?? '')

    return params.toString()
}

interface LambdaResponse {
    body: { data: string }
}
